<template>
  <loader v-if="fetchResumeItemPending"/>
  <v-container v-else>
    <v-row>
      <v-col :cols="12">
        <div class="d-flex align-center">
          <v-btn text icon @click="() => $router.go(-1)">
            <v-icon>chevron_left</v-icon>
          </v-btn>
          <h1>
            {{ form.id > 0 ? $t('texts.editExperience') : $t('texts.addExperience') }}
          </h1>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-alert type="info" :value="isSystemGenerated || isApproved">
            {{ $t('texts.automaticallyAddedOnReceiptOfJob') }}
          </v-alert>
          <v-alert
            type="error"
            v-if="form.status === 'DECLINED'"
            :value="form.status === 'DECLINED'"
          >
            <strong>{{ $t(`texts.declineEmployeeTypes.${form.decline.reason}`) }}</strong>
            <p class="mb-0 pb-0">
              {{ form.decline.explanation}}
            </p>
            <v-divider color="white" class="my-2"/>
            <p class="mb-0 pb-0">
              {{ $t('texts.correctDeclinedJobEntry') }}
            </p>
          </v-alert>
          <v-card-text>
            <v-form>
              <v-container grid-list-xl class="pa-0">
                <v-row>
                  <v-col :cols="12" :md="8">
                    <v-text-field
                      :label="$t('texts.title')"
                      v-model="form.title"
                      required
                      :error-messages="errors.collect('title')"
                      v-validate="'required'"
                      data-vv-name="title"
                      :data-vv-as="$t('texts.title')"
                      :disabled="isSystemGenerated || isApproved"
                    />
                  </v-col>
                  <v-col :cols="12" :md="4">
                    <v-text-field
                      :label="$t('texts.workload')"
                      v-model="form.workload"
                      required
                      :error-messages="errors.collect('workload')"
                      v-validate="'required|numeric|min_value:1|max_value:100'"
                      data-vv-name="workload"
                      :data-vv-as="$t('texts.workload')"
                      :disabled="isSystemGenerated || isApproved"
                      suffix="%"
                      type="number"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="12">
                    <google-place-autocomplete
                      ref="googleautocomplete"
                      v-model="form.company_name"
                      :model="form"
                      modelKey="company_name"
                      :label="$t('texts.nameOfCompany')"
                      prependIcon="search"
                      :types="['establishment']"
                      required
                      :disabled="isSystemGenerated || isApproved"
                      :data-vv-as="$t('texts.nameOfCompany')"
                      :error-messages="errors.collect('company_name')"
                      :autocompleteResponse="autocompleteResponse"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="12">
                    <v-text-field
                      :label="$t('texts.street')"
                      v-model="form.street"
                      required
                      :error-messages="errors.collect('street')"
                      v-validate="'required'"
                      data-vv-name="street"
                      :data-vv-as="$t('texts.street')"
                      :disabled="isSystemGenerated || isApproved"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="12" :md="4">
                    <v-text-field
                      :label="$t('texts.zip')"
                      v-model="form.zip"
                      required
                      :error-messages="errors.collect('zip')"
                      v-validate="'required|min:4|max:5'"
                      data-vv-name="zip"
                      :data-vv-as="$t('texts.zip')"
                      :disabled="isSystemGenerated || isApproved"
                    />
                  </v-col>
                  <v-col :cols="12" :md="8">
                    <v-text-field
                      :label="$t('texts.place')"
                      v-model="form.place"
                      required
                      :error-messages="errors.collect('place')"
                      v-validate="'required'"
                      data-vv-name="place"
                      :data-vv-as="$t('texts.place')"
                      :disabled="isSystemGenerated || isApproved"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="12">
                    <v-autocomplete
                      :items="countries"
                      v-model="form.country"
                      :label="$t('texts.country')"
                      item-value="iso"
                      item-text="name"
                      required
                      :error-messages="errors.collect('country')"
                      v-validate="'required'"
                      data-vv-name="country"
                      :data-vv-as="$t('texts.country')"
                      :disabled="isSystemGenerated || isApproved"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="12" :md="5">
                    <v-row>
                      <v-col :cols="12" class="pb-0">
                        <strong>{{ $t('texts.startOfWork') }}</strong>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col :cols="12" :md="4">
                        <v-autocomplete
                          v-model="form.start_year"
                          :label="$t('texts.year')"
                          :items="years"
                          required
                          :error-messages="errors.collect('start_year')"
                          v-validate="'required'"
                          data-vv-name="start_year"
                          :data-vv-as="$t('texts.year')"
                          :disabled="isSystemGenerated || isApproved"
                          @change="checkStartDate(); setDefaultEndDate();"
                        />
                      </v-col>
                      <v-col :cols="12" :md="4">
                        <v-autocomplete
                          v-model="form.start_month"
                          :label="$tc('texts.months.label', 1)"
                          :items="months"
                          required
                          :error-messages="errors.collect('start_month')"
                          v-validate="'required'"
                          data-vv-name="start_month"
                          :data-vv-as="$tc('texts.months.label', 1)"
                          :disabled="isSystemGenerated || isApproved"
                          @change="checkStartDate(); setDefaultEndDate();"
                        />
                      </v-col>
                      <v-col :cols="12" :md="4">
                        <v-autocomplete
                          v-if="fullDateStart || fullDateEnd"
                          v-model="form.start_day"
                          :label="$tc('texts.day', 1)"
                          :items="startDays"
                          required
                          :error-messages="errors.collect('texts')"
                          v-validate="'required'"
                          data-vv-name="texts"
                          :data-vv-as="$tc('texts.day', 1)"
                          :disabled="isSystemGenerated || isApproved"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col :cols="12" :md="5">
                    <v-row v-if="!currentJob">
                      <v-col :cols="12" class="pb-0">
                        <strong>{{ $t('texts.endOfWork') }}</strong>
                        <template v-if="!isDatesValid">
                          <br><span class="error--text" >{{ $t('texts.alert.endOfWorkError') }}</span>
                        </template>
                      </v-col>
                    </v-row>
                    <v-row v-if="!currentJob">
                      <v-col :cols="12" :md="4">
                        <v-autocomplete
                          v-model="form.end_year"
                          :label="$t('texts.year')"
                          :items="years"
                          required
                          :error-messages="errors.collect('end_year')"
                          v-validate="'required'"
                          data-vv-name="end_year"
                          :data-vv-as="$t('texts.year')"
                          :disabled="isSystemGenerated || isApproved"
                          @change="checkEndDate()"
                        />
                      </v-col>
                      <v-col :cols="12" :md="4">
                        <v-autocomplete
                          v-model="form.end_month"
                          :label="$tc('texts.months.label', 1)"
                          :items="months"
                          required
                          :error-messages="errors.collect('end_month')"
                          v-validate="'required'"
                          data-vv-name="end_month"
                          :data-vv-as="$tc('texts.months.label', 1)"
                          :disabled="isSystemGenerated || isApproved"
                          @change="checkEndDate()"
                        />
                      </v-col>
                      <v-col :cols="12" :md="4">
                        <v-autocomplete
                          v-if="fullDateStart || fullDateEnd"
                          v-model="form.end_day"
                          :label="$tc('texts.day', 1)"
                          :items="endDays"
                          required
                          :error-messages="errors.collect('end_day')"
                          v-validate="'required'"
                          data-vv-name="end_day"
                          :data-vv-as="$t('texts.year')"
                          :disabled="isSystemGenerated || isApproved"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col :cols="12" :md="2">
                    <v-row v-if="$vuetify.breakpoint.mdAndUp">
                      <v-col/>
                    </v-row>
                    <v-row>
                      <v-col :cols="12">
                        <v-checkbox
                          v-model="currentJob"
                          :label="$t('texts.noEndDate')"
                          :true-value="true"
                          :false-value="false"
                          color="primary"
                          :disabled="isSystemGenerated || form.cooperationAgreements"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="12">
                    <v-textarea
                      :outlined="isSystemGenerated || isApproved"
                      :label="$t('texts.description')"
                      v-model="form.description"
                    />
                  </v-col>
                </v-row>
                <v-divider/>
                <v-row>
                  <v-col :cols="12">
                    <file-upload
                      :documents="form.documents"
                      @interface="editedDocuments"
                      :uploadType="`jobsUpload-${form.id}`"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-card-text>
          <v-divider/>
          <v-card-actions class="py-3 px-5">
            <template v-if="form.id > 0">
              <v-tooltip
                v-if="showDeleteWarning"
                top
                class="mr-3"
                max-width="300px"
                color="warning"
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    color="red darken-2"
                    class="white--text"
                    @click="deleteItem()"
                    :disabled="storeResumeItemPending || deleteResumeItemPending || showDeleteWarning"
                    :loading="deleteResumeItemPending"
                  >
                    {{ $t('texts.deleteEntry') }}
                    <v-icon right class="white--text">delete</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('texts.deleteEntryInfo') }}</span>
              </v-tooltip>
              <v-btn
                v-if="isSystemGenerated || isApproved"
                outlined
                @click="showReportProblemDialog()"
                class="ml-2"
              >
                {{ $t('texts.reportError') }}
              </v-btn>
            </template>
            <v-spacer/>
            <v-btn
              color="primary" class="white--text"
              @click="handleFormSubmit()"
              :disabled="storeResumeItemPending || deleteResumeItemPending"
              :loading="storeResumeItemPending"
            >
              {{ form.id > 0 ? $t('texts.saveChanges') : $t('texts.save') }}
              <v-icon right class="white--text">check_circle</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <confirmation-dialog ref="confirm"/>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import moment from 'moment';
import FileUpload from '@/components/_organisms/FileUpload.vue';
import GooglePlaceAutocomplete from '@/components/_organisms/GooglePlaceAutocomplete.vue';
import ConfirmationDialog from '@/components/_organisms/ConfirmationDialog.vue';

const { mapState: mapEmployeeState, mapActions: mapEmployeeActions } = createNamespacedHelpers('employee');
const { mapState: mapGeneralState } = createNamespacedHelpers('general');
const { mapActions: mapMessageActions } = createNamespacedHelpers('messages');

export default {
  name: 'EditResumeJob',
  $_veeValidate: {
    validator: 'new',
  },
  components: {
    FileUpload,
    GooglePlaceAutocomplete,
    ConfirmationDialog,
  },
  data() {
    return {
      form: {
        id: null,
        title: '',
        workload: null,
        google_place_id: '',
        company_name: '',
        zip: '',
        place: '',
        street: '',
        street_number: '',
        country: '',
        category: '',
        start_day: null,
        start_month: null,
        start_year: null,
        status: '',
        end_day: null,
        end_month: null,
        end_year: null,
        description: '',
        documents: [],
        cooperationAgreements: false,
      },
      documents: [],
      currentJob: false,
      fullDateStart: false,
      fullDateEnd: false,
    };
  },
  created() {
    this.reset();
    if (this.$route.params.item) {
      this.setResumeItem(this.$route.params.item);
    } else if (this.$route.params.id) {
      const payload = {
        type: this.$route.meta.resumeType,
        id: this.$route.params.id,
      };
      this.fetchResumeItem(payload).then((response) => {
        this.setResumeItem(response.data.data);
      });
    } else {
      this.currentJob = false;
    }
  },
  computed: {
    ...mapGeneralState([
      'countries',
    ]),
    ...mapEmployeeState([
      'fetchResumeItemPending',
      'storeResumeItemPending',
      'deleteResumeItemPending',
    ]),
    months() {
      const items = [
        { value: 1, text: this.$i18n.t('texts.months.1') },
        { value: 2, text: this.$i18n.t('texts.months.2') },
        { value: 3, text: this.$i18n.t('texts.months.3') },
        { value: 4, text: this.$i18n.t('texts.months.4') },
        { value: 5, text: this.$i18n.t('texts.months.5') },
        { value: 6, text: this.$i18n.t('texts.months.6') },
        { value: 7, text: this.$i18n.t('texts.months.7') },
        { value: 8, text: this.$i18n.t('texts.months.8') },
        { value: 9, text: this.$i18n.t('texts.months.9') },
        { value: 10, text: this.$i18n.t('texts.months.10') },
        { value: 11, text: this.$i18n.t('texts.months.11') },
        { value: 12, text: this.$i18n.t('texts.months.12') },
      ];
      return items;
    },
    isSystemGenerated() {
      return this.form.status === 'SYSTEM';
    },
    isApproved() {
      return this.form.status === 'APPROVED';
    },
    years() {
      const possibleYears = [];
      const currentYear = moment().year();
      const firstPossibleYear = currentYear - 70;
      const lastPossibleYear = currentYear + 2;
      for (let index = lastPossibleYear; firstPossibleYear <= index; index -= 1) {
        possibleYears.push(index);
      }
      return possibleYears;
    },
    startDays() {
      let daysInMonth = 31;
      if (this.form.start_year !== null && this.form.start_month !== null) {
        daysInMonth = moment([this.form.start_year, this.form.start_month - 1]).daysInMonth();
      }
      return this.getDaysInMonthItems(daysInMonth);
    },
    endDays() {
      let daysInMonth = 31;
      if (this.form.end_year !== null && this.form.end_month !== null) {
        daysInMonth = moment([this.form.end_year, this.form.end_month - 1]).daysInMonth();
      }
      return this.getDaysInMonthItems(daysInMonth);
    },
    isDatesValid() {
      const {
        start_year: startYear,
        start_month: startMonth,
        start_day: startDay,
        end_year: endYear,
        end_month: endMonth,
        end_day: endDay,
      } = this.form;
      if (startYear && startMonth && endYear && endMonth) {
        const jobStartDay = startDay === null ? 1 : startDay;
        const jobEndDay = endDay === null ? 1 : endDay;
        const jobStartDate = moment([startYear, startMonth - 1, jobStartDay]);
        const jobEndDate = moment([endYear, endMonth - 1, jobEndDay]);
        return jobEndDate.diff(jobStartDate, 'days') > 0;
      }
      return true;
    },
    showDeleteWarning() {
      return this.isSystemGenerated || this.isApproved;
    },
  },
  methods: {
    ...mapEmployeeActions([
      'fetchResumeItem',
      'storeResumeItem',
      'deleteResumeItem',
    ]),
    ...mapMessageActions([
      'showMessageValidateError',
    ]),
    reset() {
      this.form = {
        id: null,
        title: '',
        workload: null,
        google_place_id: '',
        company_name: '',
        zip: '',
        place: '',
        street: '',
        street_number: '',
        country: '',
        category: '',
        start_day: null,
        start_month: null,
        start_year: null,
        status: '',
        end_day: null,
        end_month: null,
        end_year: null,
        description: '',
        documents: [],
        cooperationAgreements: false,
      };
      this.currentJob = false;
      this.fullDateStart = false;
      this.fullDateEnd = false;
      this.$validator.reset();
    },
    getDaysInMonthItems(daysInMonth) {
      const days = [];
      for (let index = 1; index <= daysInMonth; index += 1) {
        days.push(index);
      }
      return days;
    },
    setResumeItem(item) {
      if (item.id) {
        if (item.start_day) {
          this.fullDateStart = true;
        }
        Object.assign(this.form, item);
        this.currentJob = !item.end_year && !item.end_month && !item.end_day;
        if (this.$refs.googleautocomplete) {
          this.$refs.googleautocomplete.update(item.place);
        }
      }
    },
    editedDocuments(editedDocuments) {
      this.form.documents = editedDocuments;
    },
    autocompleteResponse(address) {
      this.form.google_place_id = address.place_id;
      this.form.company_name = address.name;
      this.form.zip = address.postal_code;
      this.form.place = address.locality || address.sublocality_level_1;
      this.form.street = address.route;
      this.form.street_number = address.street_number;
      this.form.country = address.country;
      this.$refs.googleautocomplete.update(this.form.company_name);
    },
    showReportProblemDialog() {
      this.$eventBus.$emit('show-report-problem-dialog', this.form.id);
    },
    checkStartDate() {
      if (this.form.start_year !== null && this.form.start_month !== null) {
        const cutOffDate = moment().subtract(3, 'years');
        const startDate = moment([this.form.start_year, this.form.start_month, 1]);
        this.fullDateStart = startDate.diff(cutOffDate, 'days') > 0;
      } else {
        this.fullDateStart = false;
      }
    },
    checkEndDate() {
      if (this.form.end_year !== null && this.form.end_month !== null) {
        const cutOffDate = moment().subtract(2, 'years');
        const endDate = moment([this.form.end_year, this.form.end_month, 1]);
        this.fullDateEnd = endDate.diff(cutOffDate, 'days') > 0;
      } else {
        this.fullDateEnd = false;
      }
    },
    setDefaultEndDate() {
      if (
        this.form.start_year !== null && this.form.start_month !== null
        && this.form.end_year === null && this.form.end_month === null
      ) {
        const endDate = moment([this.form.start_year, this.form.start_month - 1, 1]).add(1, 'M');
        this.form.end_year = moment(endDate).year();
        this.form.end_month = moment(endDate).month() + 1;
      }
    },
    handleFormSubmit() {
      if (this.currentJob) {
        this.form.end_year = null;
        this.form.end_month = null;
        this.form.end_day = null;
      }
      if (!this.fullDateStart && !this.fullDateEnd) {
        this.form.start_day = null;
        this.form.end_day = null;
      }
      this.$validator.validateAll().then((result) => {
        if (result) {
          const payload = {
            type: 'jobs',
            item: this.form,
            successMessageKey: this.form.id ? 'alert.updateResumeJobsSuccessful' : 'alert.addResumeJobsSuccessful',
            errorMessageKey: this.form.id ? 'alert.updateResumeJobsError' : 'alert.addResumeJobsError',
          };
          this.storeResumeItem(payload)
            .then(() => {
              this.$router.go(-1);
              this.$eventBus.$emit('clear-file-upload');
            });
        } else {
          this.showMessageValidateError();
        }
      });
    },
    async deleteItem() {
      const confirmDialog = async () => this.$refs.confirm.open({
        title: 'deleteEntry',
        message: 'deleteEntryQuestion',
        confirm: 'delete',
        reject: 'cancel',
      });
      if (await confirmDialog()) {
        const payload = {
          type: 'jobs',
          item: this.form,
          successMessageKey: 'alert.deleteResumeJobsSuccessful',
          errorMessageKey: 'alert.deleteResumeJobsError',
        };
        this.deleteResumeItem(payload)
          .then(() => {
            this.$router.go(-1);
          });
      }
    },
  },
};
</script>
